import { memo, forwardRef, useCallback, useMemo, useState } from 'react';
import {
  MenuItem,
  InputLabel,
  FormHelperText,
  InputAdornment,
  Chip,
  Box,
  ListSubheader,
  TextField,
} from '@mui/material';
import { CheckCircle, Error, Warning, Search as SearchIcon } from '@mui/icons-material';
import { SelectComponentProps, SelectOption } from './Select.types';
import { StyledFormControl, StyledSelect } from './Select.styles';

/**
 * Select Component
 *
 * Professional select/dropdown component following the green/teal design system.
 * Fully memoized for optimal performance in large ERP applications.
 *
 * @example
 * ```tsx
 * // Basic select
 * <Select
 *   label="Department"
 *   options={departments}
 *   value={selectedDept}
 *   onChange={(e) => setSelectedDept(e.target.value)}
 * />
 *
 * // Multi-select
 * <Select
 *   label="Skills"
 *   options={skills}
 *   multiple
 *   values={selectedSkills}
 *   onChange={(e) => setSelectedSkills(e.target.value)}
 * />
 *
 * // With search
 * <Select
 *   label="Country"
 *   options={countries}
 *   searchable
 *   placeholder="Select a country"
 * />
 * ```
 */
export const Select = memo(
  forwardRef<HTMLDivElement, SelectComponentProps>(
    function Select(
      {
        variant = 'outlined',
        size = 'md',
        label,
        placeholder,
        helperText,
        validationState = 'default',
        error = false,
        errorMessage,
        successMessage,
        options = [],
        startAdornment,
        endAdornment,
        disabled = false,
        readOnly = false,
        required = false,
        fullWidth = true,
        multiple = false,
        value,
        values,
        defaultValue,
        onChange,
        onFocus,
        onBlur,
        className,
        'data-testid': dataTestId,
        name,
        id,
        autoFocus = false,
        searchable = false,
        searchPlaceholder = 'Search...',
        renderValue,
        maxMenuHeight = 300,
        ...props
      },
      ref
    ) {
      // Search state for searchable selects
      const [searchText, setSearchText] = useState('');

      // Determine validation state
      const finalValidationState = useMemo(() => {
        if (error || errorMessage) return 'error';
        if (successMessage) return 'success';
        return validationState;
      }, [error, errorMessage, successMessage, validationState]);

      // Determine helper text
      const finalHelperText = useMemo(() => {
        if (errorMessage) return errorMessage;
        if (successMessage) return successMessage;
        return helperText;
      }, [errorMessage, successMessage, helperText]);

      // Get validation icon
      const validationIcon = useMemo(() => {
        switch (finalValidationState) {
          case 'error':
            return <Error fontSize="small" />;
          case 'success':
            return <CheckCircle fontSize="small" />;
          case 'warning':
            return <Warning fontSize="small" />;
          default:
            return null;
        }
      }, [finalValidationState]);

      // Filter options based on search text
      const filteredOptions = useMemo(() => {
        if (!searchable || !searchText) return options;

        return options.filter((option) =>
          option.label.toLowerCase().includes(searchText.toLowerCase())
        );
      }, [options, searchText, searchable]);

      // Group options by group property
      const groupedOptions = useMemo(() => {
        const groups: { [key: string]: SelectOption[] } = {};
        const ungrouped: SelectOption[] = [];

        filteredOptions.forEach((option) => {
          if (option.group) {
            if (!groups[option.group]) {
              groups[option.group] = [];
            }
            groups[option.group].push(option);
          } else {
            ungrouped.push(option);
          }
        });

        return { groups, ungrouped };
      }, [filteredOptions]);

      // Memoize change handler
      const handleChange = useCallback(
        (event: any, child?: any) => {
          onChange?.(event, child);
        },
        [onChange]
      );

      // Memoize search handler
      const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
      }, []);

      // Clear search when menu closes
      const handleClose = useCallback(() => {
        setSearchText('');
      }, []);

      // Memoize adornments
      const startAdornmentElement = useMemo(() => {
        if (!startAdornment) return undefined;
        return <InputAdornment position="start">{startAdornment}</InputAdornment>;
      }, [startAdornment]);

      const endAdornmentElement = useMemo(() => {
        if (!endAdornment && validationIcon && finalValidationState !== 'default') {
          return <InputAdornment position="end">{validationIcon}</InputAdornment>;
        }
        if (!endAdornment) return undefined;
        return <InputAdornment position="end">{endAdornment}</InputAdornment>;
      }, [endAdornment, validationIcon, finalValidationState]);

      // Custom render for multi-select
      const defaultRenderValue = useCallback(
        (selected: any) => {
          if (multiple && Array.isArray(selected)) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((val) => {
                  const option = options.find((opt) => opt.value === val);
                  return (
                    <Chip
                      key={val}
                      label={option?.label || val}
                      size="small"
                      icon={option?.icon as any}
                    />
                  );
                })}
              </Box>
            );
          }

          const option = options.find((opt) => opt.value === selected);
          return option?.label || selected;
        },
        [multiple, options]
      );

      // Generate label ID
      const labelId = useMemo(() => `${id || name || 'select'}-label`, [id, name]);

      // Determine current value
      const currentValue = multiple ? values || [] : value || '';

      // Render menu items
      const menuItems = useMemo(() => {
        const items: React.ReactNode[] = [];

        // Add search field if searchable
        if (searchable) {
          items.push(
            <ListSubheader key="search" sx={{ backgroundColor: 'background.paper' }}>
              <TextField
                size="small"
                autoFocus
                placeholder={searchPlaceholder}
                fullWidth
                value={searchText}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  // Prevent menu from closing when typing
                  if (e.key !== 'Escape') {
                    e.stopPropagation();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </ListSubheader>
          );
        }

        // Add ungrouped options
        groupedOptions.ungrouped.forEach((option) => {
          items.push(
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.icon && (
                <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  {option.icon}
                </Box>
              )}
              {option.label}
            </MenuItem>
          );
        });

        // Add grouped options
        Object.entries(groupedOptions.groups).forEach(([groupName, groupOptions]) => {
          items.push(
            <ListSubheader key={`group-${groupName}`}>{groupName}</ListSubheader>
          );
          groupOptions.forEach((option) => {
            items.push(
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.icon && (
                  <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    {option.icon}
                  </Box>
                )}
                {option.label}
              </MenuItem>
            );
          });
        });

        return items;
      }, [searchable, searchPlaceholder, searchText, handleSearchChange, groupedOptions]);

      return (
        <StyledFormControl
          ref={ref}
          selectSize={size}
          validationState={finalValidationState}
          variant={variant}
          error={finalValidationState === 'error'}
          disabled={disabled}
          required={required}
          fullWidth={fullWidth}
          className={className}
        >
          {label && <InputLabel id={labelId}>{label}</InputLabel>}

          <StyledSelect
            labelId={labelId}
            id={id}
            name={name}
            value={currentValue}
            defaultValue={defaultValue}
            onChange={handleChange}
            onClose={handleClose}
            onFocus={onFocus}
            onBlur={onBlur}
            label={label}
            multiple={multiple}
            autoFocus={autoFocus}
            displayEmpty={!!placeholder}
            renderValue={renderValue || (placeholder ? (val) => {
              if ((multiple && Array.isArray(val) && val.length === 0) || (!multiple && !val)) {
                return <span style={{ color: '#9E9E9E' }}>{placeholder}</span>;
              }
              return defaultRenderValue(val);
            } : defaultRenderValue)}
            readOnly={readOnly}
            data-testid={dataTestId}
            startAdornment={startAdornmentElement}
            endAdornment={endAdornmentElement}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: maxMenuHeight,
                },
              },
            }}
            {...props}
          >
            {menuItems}
          </StyledSelect>

          {finalHelperText && <FormHelperText>{finalHelperText}</FormHelperText>}
        </StyledFormControl>
      );
    }
  )
);

Select.displayName = 'Select';

export default Select;
